import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useOrders } from "./useOrders";
import { useStaff } from "./useStaff";
import { OrderFilters } from "../types/order";
import { useOrderFilters } from "./useOrderFilters";
import { useOrderPagination } from "./useOrderPagination";

const DEFAULT_PAGE_INDEX = 1;

export const useSaloonOrder = () => {
  const navigate = useNavigate();
  const { orders, loading, paginationData, fetchOrders } = useOrders();
  const { staffList, loading: staffLoading } = useStaff();

  const { searchTerm, setSearchTerm, filters, setFilters, appliedFilters, debouncedSearchTerm, applyFilters, resetFilters, getFilterPlaceholder } = useOrderFilters();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, setPage } = useOrderPagination(10, (pageNumber, pageSize) => {
    loadOrders(pageNumber, pageSize, searchTerm, appliedFilters);
  });

  const [viewLoading, setViewLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  const loadOrders = useCallback(
    (pageNumber: number, pageSize: number, search: string, filterValues: any) => {
      const apiFilters: OrderFilters = { ...filterValues, searchTerm: search };
      fetchOrders(pageNumber, pageSize, apiFilters);
    },
    [fetchOrders]
  );

  const handleSearch = useCallback(() => {
    setPage(0);
    applyFilters();
    loadOrders(DEFAULT_PAGE_INDEX, rowsPerPage, searchTerm, filters);
    setDropdownOpen(false);
  }, [applyFilters, filters, rowsPerPage, searchTerm, loadOrders, setPage]);

  const viewOrderPage = useCallback(
    (orderId: string) => {
      setViewLoading(true);
      const order = orders.find((o) => o.orders[0].orderId === orderId);
      if (order) {
        navigate(`/v1/saloon-order-display/${orderId}`, { state: { orderData: order } });
      } else {
        toast.error("Order data not found");
      }
      setViewLoading(false);
    },
    [navigate, orders]
  );

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      setPage(0);
      loadOrders(DEFAULT_PAGE_INDEX, rowsPerPage, debouncedSearchTerm, appliedFilters);
    }
  }, [debouncedSearchTerm, rowsPerPage, appliedFilters, loadOrders, setPage]);

  useEffect(() => {
    loadOrders(DEFAULT_PAGE_INDEX, rowsPerPage, searchTerm, appliedFilters);
  }, []); // initial load

  return {
    orders,
    loading,
    paginationData,
    staffList,
    staffLoading,

    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    appliedFilters,
    getFilterPlaceholder,

    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,

    viewLoading,
    dropdownOpen,
    setDropdownOpen,
    exportLoading,
    setExportLoading,
    importLoading,
    setImportLoading,

    handleSearch,
    resetFilters,
    viewOrderPage,
  };
};
